<template>
  <div class="modal fade" id="integrationDetailModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="mdi mdi-link-variant me-2"></i>
            Integration Details - {{ integrationDetail.systemName }}
          </h4>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <!-- Integration Overview -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="card border">
                <div class="card-header bg-light">
                  <h5 class="mb-0">Integration Overview</h5>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-4">
                      <div class="integration-info">
                        <div class="d-flex align-items-center mb-3">
                          <div class="avatar-lg rounded-circle bg-primary me-3">
                            <i :class="getSystemIcon(integrationDetail.category)" class="text-white fs-2"></i>
                          </div>
                          <div>
                            <h5 class="mb-1">{{ integrationDetail.systemName }}</h5>
                            <p class="text-muted mb-0">{{ integrationDetail.description }}</p>
                          </div>
                        </div>

                        <div class="status-badge mb-3">
                          <span class="badge fs-6" :class="getStatusClass(integrationDetail.status)">
                            {{ integrationDetail.status }}
                          </span>
                          <span class="badge bg-info ms-2">{{ formatCategory(integrationDetail.category) }}</span>
                        </div>
                      </div>
                    </div>

                    <div class="col-md-4">
                      <table class="table table-sm table-borderless">
                        <tr>
                          <td><strong>API Endpoint:</strong></td>
                          <td class="text-truncate">{{ integrationDetail.apiEndpoint }}</td>
                        </tr>
                        <tr>
                          <td><strong>Sync Frequency:</strong></td>
                          <td>{{ integrationDetail.syncFrequency }}</td>
                        </tr>
                        <tr>
                          <td><strong>Last Sync:</strong></td>
                          <td>{{ integrationDetail.lastSync }}</td>
                        </tr>
                        <tr>
                          <td><strong>Data Flow:</strong></td>
                          <td>
                            <div class="d-flex align-items-center">
                              <i v-if="integrationDetail.dataFlow?.includes('IN')" class="mdi mdi-arrow-down text-success me-1" title="Data In"></i>
                              <i v-if="integrationDetail.dataFlow?.includes('OUT')" class="mdi mdi-arrow-up text-warning me-1" title="Data Out"></i>
                              <i v-if="integrationDetail.dataFlow?.includes('BIDIRECTIONAL')" class="mdi mdi-arrow-up-down text-info me-1" title="Bidirectional"></i>
                              {{ formatDataFlow(integrationDetail.dataFlow) }}
                            </div>
                          </td>
                        </tr>
                      </table>
                    </div>

                    <div class="col-md-4">
                      <div class="health-monitor">
                        <h6 class="mb-3">System Health</h6>
                        <div class="progress mb-2" style="height: 15px;">
                          <div class="progress-bar" :class="getHealthClass(integrationDetail.health)"
                               :style="{ width: integrationDetail.health + '%' }">
                            {{ integrationDetail.health }}%
                          </div>
                        </div>
                        <div class="d-flex justify-content-between text-muted">
                          <small>Poor</small>
                          <small>Excellent</small>
                        </div>

                        <div class="mt-3">
                          <div class="d-flex justify-content-between mb-1">
                            <small>Uptime:</small>
                            <small class="text-success fw-bold">99.8%</small>
                          </div>
                          <div class="d-flex justify-content-between mb-1">
                            <small>Avg Response:</small>
                            <small class="text-info fw-bold">{{ mockMetrics.avgResponse }}ms</small>
                          </div>
                          <div class="d-flex justify-content-between">
                            <small>Last Error:</small>
                            <small class="text-muted">{{ mockMetrics.lastError }}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Tabs for different sections -->
          <ul class="nav nav-tabs" role="tablist">
            <li class="nav-item">
              <a class="nav-link active" data-bs-toggle="tab" href="#configuration" role="tab">
                <i class="mdi mdi-cog me-1"></i>
                Configuration
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" data-bs-toggle="tab" href="#authentication" role="tab">
                <i class="mdi mdi-shield-key me-1"></i>
                Authentication
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" data-bs-toggle="tab" href="#data-mapping" role="tab">
                <i class="mdi mdi-map me-1"></i>
                Data Mapping
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" data-bs-toggle="tab" href="#monitoring" role="tab">
                <i class="mdi mdi-chart-line me-1"></i>
                Monitoring
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" data-bs-toggle="tab" href="#logs" role="tab">
                <i class="mdi mdi-file-document me-1"></i>
                Logs
              </a>
            </li>
          </ul>

          <div class="tab-content mt-3">
            <!-- Configuration Tab -->
            <div class="tab-pane fade show active" id="configuration" role="tabpanel">
              <form @submit.prevent="saveConfiguration">
                <div class="row">
                  <div class="col-md-6">
                    <div class="card">
                      <div class="card-header bg-light">
                        <h6 class="mb-0">Basic Configuration</h6>
                      </div>
                      <div class="card-body">
                        <div class="mb-3">
                          <label class="form-label">System Name</label>
                          <input v-model="configForm.systemName" type="text" class="form-control" readonly>
                        </div>

                        <div class="mb-3">
                          <label class="form-label">API Endpoint <span class="text-danger">*</span></label>
                          <input v-model="configForm.apiEndpoint" type="url" class="form-control" required>
                        </div>

                        <div class="mb-3">
                          <label class="form-label">Sync Frequency</label>
                          <select v-model="configForm.syncFrequency" class="form-select">
                            <option value="REAL_TIME">Real-time</option>
                            <option value="HOURLY">Hourly</option>
                            <option value="DAILY">Daily</option>
                            <option value="WEEKLY">Weekly</option>
                            <option value="MANUAL">Manual Only</option>
                          </select>
                        </div>

                        <div class="mb-3">
                          <label class="form-label">Timeout (seconds)</label>
                          <input v-model="configForm.timeout" type="number" class="form-control" min="5" max="300">
                        </div>

                        <div class="mb-3">
                          <label class="form-label">Retry Attempts</label>
                          <input v-model="configForm.retryAttempts" type="number" class="form-control" min="0" max="10">
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="card">
                      <div class="card-header bg-light">
                        <h6 class="mb-0">Advanced Settings</h6>
                      </div>
                      <div class="card-body">
                        <div class="mb-3">
                          <label class="form-label">Data Flow Direction</label>
                          <div class="form-check-group">
                            <div class="form-check">
                              <input v-model="configForm.dataFlow" class="form-check-input"
                                     type="checkbox" value="IN" id="flowIn">
                              <label class="form-check-label" for="flowIn">Inbound Data</label>
                            </div>
                            <div class="form-check">
                              <input v-model="configForm.dataFlow" class="form-check-input"
                                     type="checkbox" value="OUT" id="flowOut">
                              <label class="form-check-label" for="flowOut">Outbound Data</label>
                            </div>
                          </div>
                        </div>

                        <div class="mb-3">
                          <div class="form-check form-switch">
                            <input v-model="configForm.enableLogging" class="form-check-input"
                                   type="checkbox" id="enableLogging">
                            <label class="form-check-label" for="enableLogging">Enable Detailed Logging</label>
                          </div>
                        </div>

                        <div class="mb-3">
                          <div class="form-check form-switch">
                            <input v-model="configForm.enableAlerts" class="form-check-input"
                                   type="checkbox" id="enableAlerts">
                            <label class="form-check-label" for="enableAlerts">Enable Error Alerts</label>
                          </div>
                        </div>

                        <div class="mb-3">
                          <div class="form-check form-switch">
                            <input v-model="configForm.enableWebhooks" class="form-check-input"
                                   type="checkbox" id="enableWebhooks">
                            <label class="form-check-label" for="enableWebhooks">Enable Webhooks</label>
                          </div>
                        </div>

                        <div v-if="configForm.enableWebhooks" class="mb-3">
                          <label class="form-label">Webhook URL</label>
                          <input v-model="configForm.webhookUrl" type="url" class="form-control"
                                 placeholder="https://your-webhook-endpoint.com">
                        </div>

                        <div class="mb-3">
                          <label class="form-label">Rate Limit (requests/minute)</label>
                          <input v-model="configForm.rateLimit" type="number" class="form-control" min="1" max="1000">
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="d-flex justify-content-end">
                  <button type="submit" :disabled="saving" class="btn btn-primary">
                    <span v-if="!saving">Save Configuration</span>
                    <span v-else>
                      <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                      Saving...
                    </span>
                  </button>
                </div>
              </form>
            </div>

            <!-- Authentication Tab -->
            <div class="tab-pane fade" id="authentication" role="tabpanel">
              <div class="row">
                <div class="col-md-6">
                  <div class="card">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">Authentication Method</h6>
                    </div>
                    <div class="card-body">
                      <div class="mb-3">
                        <label class="form-label">Authentication Type</label>
                        <select v-model="authForm.authType" @change="handleAuthTypeChange" class="form-select">
                          <option value="API_KEY">API Key</option>
                          <option value="OAUTH2">OAuth 2.0</option>
                          <option value="BASIC_AUTH">Basic Authentication</option>
                          <option value="BEARER_TOKEN">Bearer Token</option>
                          <option value="CUSTOM">Custom Headers</option>
                        </select>
                      </div>

                      <!-- API Key Authentication -->
                      <div v-if="authForm.authType === 'API_KEY'">
                        <div class="mb-3">
                          <label class="form-label">API Key</label>
                          <div class="input-group">
                            <input v-model="authForm.apiKey" :type="showApiKey ? 'text' : 'password'"
                                   class="form-control" placeholder="Enter API key">
                            <button @click="showApiKey = !showApiKey" type="button" class="btn btn-outline-secondary">
                              <i :class="showApiKey ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                            </button>
                          </div>
                        </div>
                        <div class="mb-3">
                          <label class="form-label">API Key Header Name</label>
                          <input v-model="authForm.apiKeyHeader" type="text" class="form-control"
                                 placeholder="X-API-Key" value="X-API-Key">
                        </div>
                      </div>

                      <!-- OAuth 2.0 Authentication -->
                      <div v-if="authForm.authType === 'OAUTH2'">
                        <div class="mb-3">
                          <label class="form-label">Client ID</label>
                          <input v-model="authForm.clientId" type="text" class="form-control">
                        </div>
                        <div class="mb-3">
                          <label class="form-label">Client Secret</label>
                          <div class="input-group">
                            <input v-model="authForm.clientSecret" :type="showClientSecret ? 'text' : 'password'"
                                   class="form-control">
                            <button @click="showClientSecret = !showClientSecret" type="button" class="btn btn-outline-secondary">
                              <i :class="showClientSecret ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                            </button>
                          </div>
                        </div>
                        <div class="mb-3">
                          <label class="form-label">Token URL</label>
                          <input v-model="authForm.tokenUrl" type="url" class="form-control">
                        </div>
                        <div class="mb-3">
                          <label class="form-label">Scope</label>
                          <input v-model="authForm.scope" type="text" class="form-control" placeholder="read write">
                        </div>
                      </div>

                      <!-- Basic Authentication -->
                      <div v-if="authForm.authType === 'BASIC_AUTH'">
                        <div class="mb-3">
                          <label class="form-label">Username</label>
                          <input v-model="authForm.username" type="text" class="form-control">
                        </div>
                        <div class="mb-3">
                          <label class="form-label">Password</label>
                          <div class="input-group">
                            <input v-model="authForm.password" :type="showPassword ? 'text' : 'password'"
                                   class="form-control">
                            <button @click="showPassword = !showPassword" type="button" class="btn btn-outline-secondary">
                              <i :class="showPassword ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                            </button>
                          </div>
                        </div>
                      </div>

                      <!-- Bearer Token -->
                      <div v-if="authForm.authType === 'BEARER_TOKEN'">
                        <div class="mb-3">
                          <label class="form-label">Bearer Token</label>
                          <div class="input-group">
                            <input v-model="authForm.bearerToken" :type="showBearerToken ? 'text' : 'password'"
                                   class="form-control">
                            <button @click="showBearerToken = !showBearerToken" type="button" class="btn btn-outline-secondary">
                              <i :class="showBearerToken ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                            </button>
                          </div>
                        </div>
                      </div>

                      <!-- Custom Headers -->
                      <div v-if="authForm.authType === 'CUSTOM'">
                        <div class="mb-3">
                          <label class="form-label">Custom Headers</label>
                          <div v-for="(header, index) in authForm.customHeaders" :key="index" class="row mb-2">
                            <div class="col-5">
                              <input v-model="header.name" type="text" class="form-control" placeholder="Header Name">
                            </div>
                            <div class="col-5">
                              <input v-model="header.value" type="text" class="form-control" placeholder="Header Value">
                            </div>
                            <div class="col-2">
                              <button @click="removeCustomHeader(index)" type="button" class="btn btn-outline-danger">
                                <i class="mdi mdi-close"></i>
                              </button>
                            </div>
                          </div>
                          <button @click="addCustomHeader" type="button" class="btn btn-outline-primary btn-sm">
                            <i class="mdi mdi-plus me-1"></i>Add Header
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="card">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">Connection Test</h6>
                    </div>
                    <div class="card-body">
                      <div class="mb-3">
                        <button @click="testAuthentication" :disabled="testing" class="btn btn-info w-100">
                          <span v-if="!testing">
                            <i class="mdi mdi-connection me-1"></i>
                            Test Authentication
                          </span>
                          <span v-else>
                            <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                            Testing...
                          </span>
                        </button>
                      </div>

                      <!-- Test Results -->
                      <div v-if="testResult" class="alert" :class="testResult.success ? 'alert-success' : 'alert-danger'">
                        <h6 class="alert-heading">
                          <i :class="testResult.success ? 'mdi mdi-check-circle' : 'mdi mdi-alert-circle'" class="me-2"></i>
                          {{ testResult.success ? 'Connection Successful' : 'Connection Failed' }}
                        </h6>
                        <p class="mb-0">{{ testResult.message }}</p>
                        <div v-if="testResult.details" class="mt-2">
                          <small class="text-muted">
                            <strong>Response Time:</strong> {{ testResult.details.responseTime }}ms<br>
                            <strong>Status Code:</strong> {{ testResult.details.statusCode }}
                          </small>
                        </div>
                      </div>

                      <!-- Certificate Information -->
                      <div class="mt-4">
                        <h6>SSL Certificate Information</h6>
                        <div class="border rounded p-3 bg-light">
                          <div class="d-flex justify-content-between mb-2">
                            <small>SSL Status:</small>
                            <span class="badge bg-success">Valid</span>
                          </div>
                          <div class="d-flex justify-content-between mb-2">
                            <small>Expires:</small>
                            <small class="text-muted">Dec 31, 2025</small>
                          </div>
                          <div class="d-flex justify-content-between">
                            <small>Issuer:</small>
                            <small class="text-muted">Let's Encrypt</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Data Mapping Tab -->
            <div class="tab-pane fade" id="data-mapping" role="tabpanel">
              <div class="row">
                <div class="col-md-6">
                  <div class="card">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">Field Mapping Configuration</h6>
                    </div>
                    <div class="card-body">
                      <div class="mapping-container">
                        <div v-for="(mapping, index) in dataMappings" :key="index" class="mapping-row mb-3">
                          <div class="row align-items-center">
                            <div class="col-5">
                              <label class="form-label">Source Field</label>
                              <input v-model="mapping.sourceField" type="text" class="form-control"
                                     placeholder="External system field">
                            </div>
                            <div class="col-1 text-center">
                              <i class="mdi mdi-arrow-right text-primary fs-4"></i>
                            </div>
                            <div class="col-5">
                              <label class="form-label">Target Field</label>
                              <select v-model="mapping.targetField" class="form-select">
                                <option value="">Select target field</option>
                                <option v-for="field in targetFields" :key="field.value" :value="field.value">
                                  {{ field.label }}
                                </option>
                              </select>
                            </div>
                            <div class="col-1">
                              <button @click="removeMapping(index)" type="button" class="btn btn-outline-danger btn-sm">
                                <i class="mdi mdi-close"></i>
                              </button>
                            </div>
                          </div>
                          <div class="row mt-2">
                            <div class="col-5">
                              <select v-model="mapping.transformation" class="form-select form-select-sm">
                                <option value="">No transformation</option>
                                <option value="UPPERCASE">Convert to uppercase</option>
                                <option value="LOWERCASE">Convert to lowercase</option>
                                <option value="DATE_FORMAT">Format date</option>
                                <option value="CURRENCY">Format currency</option>
                                <option value="PHONE_FORMAT">Format phone number</option>
                              </select>
                            </div>
                            <div class="col-6">
                              <div class="form-check form-check-sm">
                                <input v-model="mapping.required" class="form-check-input" type="checkbox"
                                       :id="'required-' + index">
                                <label class="form-check-label" :for="'required-' + index">
                                  Required field
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>

                        <button @click="addMapping" type="button" class="btn btn-outline-primary">
                          <i class="mdi mdi-plus me-1"></i>Add Field Mapping
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="card">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">Data Validation Rules</h6>
                    </div>
                    <div class="card-body">
                      <div class="mb-3">
                        <div class="form-check form-switch">
                          <input v-model="dataValidation.enableValidation" class="form-check-input"
                                 type="checkbox" id="enableValidation">
                          <label class="form-check-label" for="enableValidation">Enable Data Validation</label>
                        </div>
                      </div>

                      <div v-if="dataValidation.enableValidation">
                        <div class="mb-3">
                          <div class="form-check">
                            <input v-model="dataValidation.validateRequired" class="form-check-input"
                                   type="checkbox" id="validateRequired">
                            <label class="form-check-label" for="validateRequired">Validate Required Fields</label>
                          </div>
                        </div>

                        <div class="mb-3">
                          <div class="form-check">
                            <input v-model="dataValidation.validateFormat" class="form-check-input"
                                   type="checkbox" id="validateFormat">
                            <label class="form-check-label" for="validateFormat">Validate Data Formats</label>
                          </div>
                        </div>

                        <div class="mb-3">
                          <div class="form-check">
                            <input v-model="dataValidation.validateDuplicates" class="form-check-input"
                                   type="checkbox" id="validateDuplicates">
                            <label class="form-check-label" for="validateDuplicates">Check for Duplicates</label>
                          </div>
                        </div>

                        <div class="mb-3">
                          <label class="form-label">Validation Action on Error</label>
                          <select v-model="dataValidation.errorAction" class="form-select">
                            <option value="SKIP">Skip invalid records</option>
                            <option value="STOP">Stop processing</option>
                            <option value="LOG">Log and continue</option>
                          </select>
                        </div>
                      </div>

                      <!-- Sample Data Preview -->
                      <div class="mt-4">
                        <h6>Sample Data Preview</h6>
                        <div class="border rounded p-3 bg-light">
                          <pre class="mb-0 text-muted small">{{ sampleDataPreview }}</pre>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Monitoring Tab -->
            <div class="tab-pane fade" id="monitoring" role="tabpanel">
              <div class="row">
                <div class="col-md-8">
                  <div class="card">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">Performance Metrics</h6>
                    </div>
                    <div class="card-body">
                      <!-- Metrics Chart Placeholder -->
                      <div class="metrics-chart">
                        <div class="row mb-4">
                          <div class="col-md-3">
                            <div class="metric-card border rounded p-3 text-center">
                              <h4 class="text-success mb-1">{{ mockMetrics.successRate }}%</h4>
                              <small class="text-muted">Success Rate</small>
                            </div>
                          </div>
                          <div class="col-md-3">
                            <div class="metric-card border rounded p-3 text-center">
                              <h4 class="text-info mb-1">{{ mockMetrics.avgResponse }}ms</h4>
                              <small class="text-muted">Avg Response</small>
                            </div>
                          </div>
                          <div class="col-md-3">
                            <div class="metric-card border rounded p-3 text-center">
                              <h4 class="text-primary mb-1">{{ mockMetrics.totalRequests }}</h4>
                              <small class="text-muted">Total Requests</small>
                            </div>
                          </div>
                          <div class="col-md-3">
                            <div class="metric-card border rounded p-3 text-center">
                              <h4 class="text-danger mb-1">{{ mockMetrics.errorCount }}</h4>
                              <small class="text-muted">Errors (24h)</small>
                            </div>
                          </div>
                        </div>

                        <!-- Placeholder for chart -->
                        <div class="chart-placeholder bg-light rounded p-4 text-center">
                          <i class="mdi mdi-chart-line text-muted" style="font-size: 3rem;"></i>
                          <p class="text-muted mt-2">Performance chart would be displayed here</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="card">
                    <div class="card-header bg-light">
                      <h6 class="mb-0">Alert Configuration</h6>
                    </div>
                    <div class="card-body">
                      <div class="mb-3">
                        <div class="form-check form-switch">
                          <input v-model="alertConfig.enableAlerts" class="form-check-input"
                                 type="checkbox" id="enableMonitoringAlerts">
                          <label class="form-check-label" for="enableMonitoringAlerts">Enable Monitoring Alerts</label>
                        </div>
                      </div>

                      <div v-if="alertConfig.enableAlerts">
                        <div class="mb-3">
                          <label class="form-label">Error Rate Threshold (%)</label>
                          <input v-model="alertConfig.errorThreshold" type="number" class="form-control"
                                 min="1" max="100" step="0.1">
                        </div>

                        <div class="mb-3">
                          <label class="form-label">Response Time Threshold (ms)</label>
                          <input v-model="alertConfig.responseThreshold" type="number" class="form-control"
                                 min="100" max="10000">
                        </div>

                        <div class="mb-3">
                          <label class="form-label">Alert Recipients</label>
                          <textarea v-model="alertConfig.recipients" class="form-control" rows="3"
                                    placeholder="admin@county.go.ke&#10;tech@county.go.ke"></textarea>
                        </div>

                        <div class="mb-3">
                          <div class="form-check">
                            <input v-model="alertConfig.emailAlerts" class="form-check-input"
                                   type="checkbox" id="emailAlerts">
                            <label class="form-check-label" for="emailAlerts">Email Alerts</label>
                          </div>
                        </div>

                        <div class="mb-3">
                          <div class="form-check">
                            <input v-model="alertConfig.smsAlerts" class="form-check-input"
                                   type="checkbox" id="smsAlerts">
                            <label class="form-check-label" for="smsAlerts">SMS Alerts</label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Logs Tab -->
            <div class="tab-pane fade" id="logs" role="tabpanel">
              <div class="row">
                <div class="col-12">
                  <div class="card">
                    <div class="card-header bg-light d-flex justify-content-between align-items-center">
                      <h6 class="mb-0">Integration Logs</h6>
                      <div>
                        <select v-model="logFilter" class="form-select form-select-sm me-2" style="width: auto; display: inline-block;">
                          <option value="ALL">All Logs</option>
                          <option value="ERROR">Errors Only</option>
                          <option value="SUCCESS">Success Only</option>
                          <option value="WARNING">Warnings Only</option>
                        </select>
                        <button @click="refreshLogs" class="btn btn-outline-primary btn-sm">
                          <i class="mdi mdi-refresh"></i>
                        </button>
                      </div>
                    </div>
                    <div class="card-body p-0">
                      <div class="logs-container" style="max-height: 400px; overflow-y: auto;">
                        <div v-for="(log, index) in filteredLogs" :key="index"
                             class="log-entry border-bottom p-3" :class="getLogClass(log.level)">
                          <div class="d-flex justify-content-between align-items-start">
                            <div class="flex-grow-1">
                              <div class="d-flex align-items-center mb-1">
                                <span class="badge me-2" :class="getLogBadgeClass(log.level)">{{ log.level }}</span>
                                <small class="text-muted">{{ log.timestamp }}</small>
                              </div>
                              <p class="mb-1">{{ log.message }}</p>
                              <div v-if="log.details" class="text-muted small">
                                <strong>Details:</strong> {{ log.details }}
                              </div>
                            </div>
                            <button @click="viewLogDetails(log)" class="btn btn-outline-info btn-sm">
                              <i class="mdi mdi-eye"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <div class="d-flex justify-content-between w-100">
            <div>
              <button @click="testConnection" :disabled="integrationDetail.isConnecting" class="btn btn-info">
                <span v-if="!integrationDetail.isConnecting">
                  <i class="mdi mdi-connection me-1"></i>
                  Test Connection
                </span>
                <span v-else>
                  <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                  Testing...
                </span>
              </button>
            </div>
            <div>
              <button type="button" class="btn btn-secondary me-2" data-bs-dismiss="modal">
                Close
              </button>
              <button @click="saveAllConfiguration" :disabled="saving" class="btn btn-primary">
                <span v-if="!saving">Save All Changes</span>
                <span v-else>
                  <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                  Saving...
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import moment from "moment";

export default {
  name: "IntegrationDetailModal",
  props: {
    integrationDetail: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      saving: false,
      testing: false,
      testResult: null,
      logFilter: 'ALL',

      // Form visibility toggles
      showApiKey: false,
      showClientSecret: false,
      showPassword: false,
      showBearerToken: false,

      // Configuration form
      configForm: {
        systemName: '',
        apiEndpoint: '',
        syncFrequency: 'HOURLY',
        timeout: 30,
        retryAttempts: 3,
        dataFlow: [],
        enableLogging: true,
        enableAlerts: true,
        enableWebhooks: false,
        webhookUrl: '',
        rateLimit: 100
      },

      // Authentication form
      authForm: {
        authType: 'API_KEY',
        apiKey: '',
        apiKeyHeader: 'X-API-Key',
        clientId: '',
        clientSecret: '',
        tokenUrl: '',
        scope: '',
        username: '',
        password: '',
        bearerToken: '',
        customHeaders: []
      },

      // Data mapping
      dataMappings: [
        {
          sourceField: 'customer_name',
          targetField: 'customerName',
          transformation: '',
          required: true
        }
      ],

      targetFields: [
        { value: 'customerName', label: 'Customer Name' },
        { value: 'customerPhone', label: 'Customer Phone' },
        { value: 'customerEmail', label: 'Customer Email' },
        { value: 'billAmount', label: 'Bill Amount' },
        { value: 'dueDate', label: 'Due Date' },
        { value: 'referenceNumber', label: 'Reference Number' }
      ],

      // Data validation
      dataValidation: {
        enableValidation: true,
        validateRequired: true,
        validateFormat: true,
        validateDuplicates: false,
        errorAction: 'LOG'
      },

      // Alert configuration
      alertConfig: {
        enableAlerts: true,
        errorThreshold: 5.0,
        responseThreshold: 5000,
        recipients: 'admin@county.go.ke\ntech@county.go.ke',
        emailAlerts: true,
        smsAlerts: false
      },

      // Mock data
      mockMetrics: {
        successRate: 98.5,
        avgResponse: 245,
        totalRequests: 12458,
        errorCount: 23,
        lastError: '2 hours ago'
      },

      sampleDataPreview: `{
  "customer_name": "John Doe Enterprises",
  "customer_phone": "+254701234567",
  "bill_amount": 15000.00,
  "due_date": "2025-02-15",
  "reference_number": "SBP250001"
}`,

      integrationLogs: [
        {
          id: 1,
          level: 'SUCCESS',
          timestamp: '2025-01-16 14:30:15',
          message: 'Data synchronization completed successfully',
          details: '450 records processed, 0 errors'
        },
        {
          id: 2,
          level: 'WARNING',
          timestamp: '2025-01-16 14:25:32',
          message: 'Slow response time detected',
          details: 'Response time: 4.2 seconds (threshold: 3.0 seconds)'
        },
        {
          id: 3,
          level: 'ERROR',
          timestamp: '2025-01-16 14:20:18',
          message: 'Authentication failed',
          details: 'Invalid API key provided. Status: 401 Unauthorized'
        },
        {
          id: 4,
          level: 'SUCCESS',
          timestamp: '2025-01-16 14:15:45',
          message: 'Connection test successful',
          details: 'Response time: 180ms, Status: 200 OK'
        },
        {
          id: 5,
          level: 'INFO',
          timestamp: '2025-01-16 14:10:22',
          message: 'Scheduled sync started',
          details: 'Processing 25 pending records'
        }
      ]
    }
  },
  computed: {
    filteredLogs() {
      if (this.logFilter === 'ALL') {
        return this.integrationLogs;
      }
      return this.integrationLogs.filter(log => log.level === this.logFilter);
    }
  },
  mounted() {
    this.initializeForms();
  },
  methods: {
    initializeForms() {
      // Initialize forms with integration data
      Object.assign(this.configForm, {
        systemName: this.integrationDetail.systemName || '',
        apiEndpoint: this.integrationDetail.apiEndpoint || '',
        syncFrequency: this.integrationDetail.syncFrequency || 'HOURLY',
        dataFlow: this.integrationDetail.dataFlow || []
      });
    },

    getSystemIcon(category) {
      switch (category) {
        case 'COUNTY_FINANCIAL':
          return 'mdi mdi-bank';
        case 'GOVERNMENT':
          return 'mdi mdi-shield-account';
        case 'PAYMENT':
          return 'mdi mdi-credit-card';
        default:
          return 'mdi mdi-link-variant';
      }
    },

    getStatusClass(status) {
      switch (status) {
        case 'ACTIVE':
          return 'bg-success';
        case 'INACTIVE':
          return 'bg-secondary';
        case 'PENDING':
          return 'bg-warning';
        case 'ERROR':
          return 'bg-danger';
        default:
          return 'bg-secondary';
      }
    },

    getHealthClass(health) {
      if (health >= 90) return 'bg-success';
      if (health >= 70) return 'bg-warning';
      if (health >= 50) return 'bg-danger';
      return 'bg-secondary';
    },

    formatCategory(category) {
      const categories = {
        'COUNTY_FINANCIAL': 'County Financial',
        'GOVERNMENT': 'Government',
        'PAYMENT': 'Payment',
        'THIRD_PARTY': 'Third Party'
      };
      return categories[category] || category;
    },

    formatDataFlow(dataFlow) {
      if (!dataFlow || dataFlow.length === 0) return 'None';
      if (dataFlow.includes('BIDIRECTIONAL')) return 'Bidirectional';
      if (dataFlow.includes('IN') && dataFlow.includes('OUT')) return 'Bidirectional';
      if (dataFlow.includes('IN')) return 'Inbound';
      if (dataFlow.includes('OUT')) return 'Outbound';
      return 'None';
    },

    handleAuthTypeChange() {
      // Reset form when auth type changes
      Object.keys(this.authForm).forEach(key => {
        if (key !== 'authType') {
          if (key === 'customHeaders') {
            this.authForm[key] = [];
          } else {
            this.authForm[key] = '';
          }
        }
      });
    },

    addCustomHeader() {
      this.authForm.customHeaders.push({ name: '', value: '' });
    },

    removeCustomHeader(index) {
      this.authForm.customHeaders.splice(index, 1);
    },

    addMapping() {
      this.dataMappings.push({
        sourceField: '',
        targetField: '',
        transformation: '',
        required: false
      });
    },

    removeMapping(index) {
      this.dataMappings.splice(index, 1);
    },

    async testAuthentication() {
      try {
        this.testing = true;
        this.testResult = null;

        // Simulate authentication test
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Random success/failure for demo
        const success = Math.random() > 0.3;

        this.testResult = {
          success: success,
          message: success
              ? 'Authentication successful! Connection established.'
              : 'Authentication failed. Please check your credentials.',
          details: success
              ? { responseTime: 245, statusCode: 200 }
              : { responseTime: 0, statusCode: 401 }
        };

        this.testing = false;
      } catch (error) {
        this.testing = false;
        this.testResult = {
          success: false,
          message: 'Connection error: ' + error.message,
          details: null
        };
      }
    },

    async testConnection() {
      try {
        this.integrationDetail.isConnecting = true;

        // Simulate connection test
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Random success for demo
        const success = Math.random() > 0.2;

        if (success) {
          alert('Connection test successful!');
        } else {
          alert('Connection test failed!');
        }

        this.integrationDetail.isConnecting = false;
      } catch (error) {
        this.integrationDetail.isConnecting = false;
        alert('Connection test error: ' + error.message);
      }
    },

    async saveConfiguration() {
      try {
        this.saving = true;

        // Simulate save
        await new Promise(resolve => setTimeout(resolve, 1500));

        alert('Configuration saved successfully!');
        this.saving = false;
      } catch (error) {
        this.saving = false;
        alert('Error saving configuration: ' + error.message);
      }
    },

    async saveAllConfiguration() {
      try {
        this.saving = true;

        // Simulate save all
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Emit configuration update
        this.$emit('integration-configured', this.integrationDetail.id, {
          ...this.configForm,
          authentication: this.authForm,
          dataMappings: this.dataMappings,
          dataValidation: this.dataValidation,
          alertConfig: this.alertConfig
        });

        this.saving = false;

        // Close modal
        const modalElement = document.getElementById('integrationDetailModal');
        if (modalElement) {
          const bootstrapModal = bootstrap.Modal.getInstance(modalElement);
          if (bootstrapModal) {
            bootstrapModal.hide();
          }
        }
      } catch (error) {
        this.saving = false;
        alert('Error saving configuration: ' + error.message);
      }
    },

    getLogClass(level) {
      switch (level) {
        case 'ERROR':
          return 'bg-danger bg-opacity-10';
        case 'WARNING':
          return 'bg-warning bg-opacity-10';
        case 'SUCCESS':
          return 'bg-success bg-opacity-10';
        default:
          return '';
      }
    },

    getLogBadgeClass(level) {
      switch (level) {
        case 'ERROR':
          return 'bg-danger';
        case 'WARNING':
          return 'bg-warning';
        case 'SUCCESS':
          return 'bg-success';
        case 'INFO':
          return 'bg-info';
        default:
          return 'bg-secondary';
      }
    },

    refreshLogs() {
      // Simulate log refresh
      console.log('Refreshing logs...');
    },

    viewLogDetails(log) {
      // Show detailed log information
      alert(`Log Details:\n\nLevel: ${log.level}\nTime: ${log.timestamp}\nMessage: ${log.message}\nDetails: ${log.details || 'None'}`);
    }
  },
  watch: {
    integrationDetail: {
      handler() {
        this.initializeForms();
      },
      immediate: true
    }
  }
}
</script>

<style scoped>
.avatar-lg {
  width: 4rem;
  height: 4rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.form-check-group {
  display: flex;
  gap: 1rem;
}

.mapping-row {
  background: #f8f9fa;
  border-radius: 0.375rem;
  padding: 1rem;
}

.metric-card {
  transition: transform 0.2s ease;
}

.metric-card:hover {
  transform: translateY(-2px);
}

.chart-placeholder {
  min-height: 200px;
}

.logs-container {
  font-family: 'Courier New', monospace;
}

.log-entry {
  transition: background-color 0.15s ease;
}

.log-entry:hover {
  background-color: rgba(0,0,0,0.02) !important;
}

.nav-tabs .nav-link {
  border-bottom: 2px solid transparent;
}

.nav-tabs .nav-link.active {
  border-bottom-color: #0d6efd;
  font-weight: 600;
}

.table-borderless td,
.table-borderless th {
  border: none;
}

.spinner-border-sm {
  width: 0.875rem;
  height: 0.875rem;
}

.modal-xl {
  max-width: 1200px;
}

.text-truncate {
  max-width: 200px;
}

.fs-2 {
  font-size: 1.5rem !important;
}

.fs-6 {
  font-size: 1rem !important;
}
</style>